import { createHash } from "node:crypto";

import { buildOrgxApiUrl, normalizeOrgxBaseUrl, resolveOrgxAuth, type ResolveOrgxAuthOptions } from "./auth.js";
import { isRecord } from "./fs.js";

export const SELF_AUDIT_SCHEMA_VERSION = "2026-04-27" as const;

export const AUDIT_DIMENSIONS = [
  "queryability",
  "proof_density",
  "loop_closure",
  "context_debt",
  "autonomy_readiness",
  "roi_visibility",
] as const;

export type AuditDimension = (typeof AUDIT_DIMENSIONS)[number];

export type FounderLoopItemType =
  | "decision"
  | "commitment"
  | "artifact"
  | "open_loop"
  | "next_action"
  | "outcome"
  | "economics";

export interface AuditWorkspaceRef {
  id: string;
  name: string;
}

export interface FounderLoopImport {
  sourceId: string;
  sourceLabel: string;
  metadata?: Record<string, unknown>;
  text: string;
}

export interface FounderLoopItem {
  type: FounderLoopItemType;
  text: string;
  sourceId: string;
  sourceLabel: string;
  evidenceRef: string;
  lineNumber: number;
}

export interface SelfAuditSignals {
  approvedWritebackTargets: number;
  artifactsWithNextActions: number;
  artifactsWithOwners: number;
  completedWorkItems: number;
  completedWorkWithProof: number;
  connectedSources: number;
  decisionsWithEvidence: number;
  economicSignals: number;
  missingSources: number;
  openLoops: number;
  outcomeLinkedItems: number;
  repeatedContextPrompts: number;
  totalArtifacts: number;
  totalContextItems: number;
  totalDecisions: number;
  unresolvedCommitments: number;
}

export type AuditScores = Record<AuditDimension, number>;

export interface AuditFinding {
  dimension: AuditDimension;
  severity: "critical" | "warning" | "info";
  title: string;
  evidence: string;
  recommendation: string;
}

export interface ArtifactInvariant {
  goal_ref: string;
  owner_ref: string;
  decision_refs: string[];
  evidence_refs: string[];
  next_action_ref: string;
  proof_requirement: "artifact" | "verification" | "external-link" | "human-review";
  retrieval_scope: string;
}

export interface SelfAuditPlan {
  artifact_type: "ai_native_self_audit_plan";
  artifact_hash: string;
  artifact_invariant: ArtifactInvariant;
  audit_scope: {
    loop: "founder";
    time_window_days: number;
    connected_sources: string[];
    missing_sources: string[];
  };
  extracted_items: FounderLoopItem[];
  findings: AuditFinding[];
  generated_at: string;
  recommended_follow_up: {
    title: string;
    summary: string;
    rollback: string;
  };
  recommended_initiative: {
    title: string;
    summary: string;
    workstreams: Array<{
      title: string;
      purpose: string;
      tasks: Array<{
        title: string;
        proof_requirement: string;
      }>;
    }>;
  };
  safe_writeback_plan: Array<{
    target: "orgx";
    action: string;
    approval: "required";
    rollback: string;
  }>;
  schema_version: typeof SELF_AUDIT_SCHEMA_VERSION;
  scores: AuditScores;
  self_critique: Array<{
    dimension: AuditDimension;
    target: number;
    score: number;
    passed: boolean;
    gap: string;
  }>;
  signals: SelfAuditSignals;
  workspace: AuditWorkspaceRef;
}

export interface BuildSelfAuditPlanInput {
  connectedSources?: string[];
  generatedAt?: string;
  imports: FounderLoopImport[];
  missingSources?: string[];
  timeWindowDays?: number;
  workspace: AuditWorkspaceRef;
}

export interface OrgxWriteOptions extends ResolveOrgxAuthOptions {
  dryRun?: boolean;
}

export interface CreateAuditArtifactOptions extends OrgxWriteOptions {
  initiativeId: string;
  markdown: string;
  plan: SelfAuditPlan;
}

export interface CreateAuditInitiativeOptions extends OrgxWriteOptions {
  plan: SelfAuditPlan;
}

export interface CreateAuditFollowUpOptions extends OrgxWriteOptions {
  initiativeId: string;
  milestoneId?: string;
  plan: SelfAuditPlan;
  workstreamId?: string;
}

export interface OrgxEntityRef {
  id: string;
  title: string;
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function ratio(numerator: number, denominator: number, fallback = 0): number {
  if (denominator <= 0) return fallback;
  return numerator / denominator;
}

function includesAny(value: string, patterns: readonly RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(value));
}

function classifyLine(line: string): FounderLoopItemType | null {
  const normalized = line.trim();
  if (!normalized) return null;

  if (/^(decision|decided|we decided|d:)\b/i.test(normalized)) return "decision";
  if (/^(commitment|committed|promise|promised|todo:|we will)\b/i.test(normalized)) return "commitment";
  if (/^(artifact|receipt|proof|shipped|implemented|commit|pr:)\b/i.test(normalized)) return "artifact";
  if (/^(open loop|gap|blocker|risk|missing|needs|unresolved)\b/i.test(normalized)) return "open_loop";
  if (/^(next action|follow[- ]?up|next step|action:)\b/i.test(normalized)) return "next_action";
  if (/^(outcome|result|impact|metric|adoption)\b/i.test(normalized)) return "outcome";
  if (/^(roi|economics|token|tokens|cost|saved|time saved|api bill)\b/i.test(normalized)) return "economics";

  return null;
}

export function extractFounderLoopItems(imports: readonly FounderLoopImport[]): FounderLoopItem[] {
  const items: FounderLoopItem[] = [];

  for (const source of imports) {
    const lines = source.text.split(/\r?\n/);
    lines.forEach((line, index) => {
      const type = classifyLine(line);
      if (!type) return;

      const lineNumber = index + 1;
      items.push({
        evidenceRef: `${source.sourceId}:L${lineNumber}`,
        lineNumber,
        sourceId: source.sourceId,
        sourceLabel: source.sourceLabel,
        text: line.trim(),
        type,
      });
    });
  }

  return items;
}

export function buildSelfAuditSignals(
  imports: readonly FounderLoopImport[],
  items: readonly FounderLoopItem[],
  options: {
    connectedSources?: readonly string[];
    missingSources?: readonly string[];
  } = {},
): SelfAuditSignals {
  const allText = imports.map((source) => source.text).join("\n");
  const lower = allText.toLowerCase();
  const decisions = items.filter((item) => item.type === "decision");
  const artifacts = items.filter((item) => item.type === "artifact");
  const commitments = items.filter((item) => item.type === "commitment");
  const nextActions = items.filter((item) => item.type === "next_action");
  const outcomes = items.filter((item) => item.type === "outcome");
  const economics = items.filter((item) => item.type === "economics");
  const proofMentions = (lower.match(/\b(proof|verified|verification|receipt|quality score|artifact)\b/g) ?? []).length;
  const ownerMentions = (lower.match(/\b(owner|dri|responsible|agent:|founder)\b/g) ?? []).length;
  const artifactNextActionMentions = artifacts.filter((item) =>
    includesAny(item.text.toLowerCase(), [/\bnext action\b/, /\bfollow[- ]?up\b/, /\brollback\b/]),
  ).length;
  const writebackMentions = (lower.match(/\b(approve|approved|writeback|rollback|create task|follow-up task)\b/g) ?? []).length;
  const repeatedContextPrompts = (
    lower.match(/\b(recap|catch you up|context again|restate|reread|remind me)\b/g) ?? []
  ).length;

  return {
    approvedWritebackTargets: Math.min(3, writebackMentions),
    artifactsWithNextActions: Math.min(artifacts.length, nextActions.length + artifactNextActionMentions),
    artifactsWithOwners: Math.min(artifacts.length, ownerMentions),
    completedWorkItems: artifacts.length,
    completedWorkWithProof: Math.min(artifacts.length, proofMentions),
    connectedSources: Math.max(options.connectedSources?.length ?? 0, imports.length),
    decisionsWithEvidence: decisions.length,
    economicSignals: economics.length + (lower.includes("token") || lower.includes("cost") || lower.includes("saved") ? 1 : 0),
    missingSources: options.missingSources?.length ?? 0,
    openLoops: items.filter((item) => item.type === "open_loop").length,
    outcomeLinkedItems: outcomes.length,
    repeatedContextPrompts,
    totalArtifacts: artifacts.length,
    totalContextItems: Math.max(items.length, allText.split(/\s+/).filter(Boolean).length),
    totalDecisions: decisions.length,
    unresolvedCommitments: Math.max(0, commitments.length - nextActions.length - outcomes.length),
  };
}

export function scoreSelfAudit(signals: SelfAuditSignals): AuditScores {
  const queryability = clampScore(
    30
    + ratio(signals.decisionsWithEvidence, Math.max(1, signals.totalDecisions), 0) * 25
    + Math.min(20, signals.connectedSources * 5)
    + Math.min(30, (signals.totalArtifacts + signals.outcomeLinkedItems + signals.approvedWritebackTargets) * 5),
  );
  const proofDensity = clampScore(
    30
    + ratio(signals.completedWorkWithProof, Math.max(1, signals.completedWorkItems), 0) * 35
    + ratio(signals.artifactsWithOwners, Math.max(1, signals.totalArtifacts), 0) * 20
    + ratio(signals.artifactsWithNextActions, Math.max(1, signals.totalArtifacts), 0) * 15,
  );
  const loopClosure = clampScore(
    40
    + Math.min(25, signals.outcomeLinkedItems * 25)
    + Math.min(35, signals.approvedWritebackTargets * 20)
    + (signals.openLoops === 0 ? 15 : Math.max(0, 15 - signals.openLoops * 3)),
  );
  const contextDebt = clampScore(
    100
    - Math.min(35, signals.repeatedContextPrompts * 10)
    - Math.min(35, signals.openLoops * 6)
    - Math.min(20, signals.missingSources * 4)
    - Math.min(10, signals.unresolvedCommitments * 3),
  );
  const autonomyReadiness = clampScore(
    35
    + Math.min(30, signals.approvedWritebackTargets * 15)
    + Math.min(20, signals.completedWorkWithProof * 5)
    + (signals.openLoops <= 1 ? 15 : 5),
  );
  const roiVisibility = clampScore(
    30
    + Math.min(40, signals.economicSignals * 18)
    + Math.min(25, signals.outcomeLinkedItems * 25)
    + Math.min(10, signals.completedWorkWithProof * 3),
  );

  return {
    autonomy_readiness: autonomyReadiness,
    context_debt: contextDebt,
    loop_closure: loopClosure,
    proof_density: proofDensity,
    queryability,
    roi_visibility: roiVisibility,
  };
}

function buildFindings(scores: AuditScores, signals: SelfAuditSignals): AuditFinding[] {
  const findings: AuditFinding[] = [];

  for (const dimension of AUDIT_DIMENSIONS) {
    const score = scores[dimension];
    if (score >= 95) {
      findings.push({
        dimension,
        evidence: `Score ${score}/100 meets the Phase 2 target.`,
        recommendation: "Keep this dimension gated by evidence so the score stays earned.",
        severity: "info",
        title: `${dimension.replace(/_/g, " ")} is at the 95+ target`,
      });
      continue;
    }

    const recommendationByDimension: Record<AuditDimension, string> = {
      autonomy_readiness: "Keep writeback approval-gated and add rollback references to every generated action.",
      context_debt: "Reduce repeated context prompts and close unresolved open loops with task or decision artifacts.",
      loop_closure: "Add outcome references and approved follow-up writebacks so planning changes after execution.",
      proof_density: "Attach owners, next actions, and verification proof to every completed work artifact.",
      queryability: "Add cited decisions, artifacts, connected sources, and retrieval scope to the audit output.",
      roi_visibility: "Add token/time/cost evidence plus an outcome review so ROI is not a narrative claim.",
    };

    findings.push({
      dimension,
      evidence: `Score ${score}/100. Signals: ${JSON.stringify({
        approvedWritebackTargets: signals.approvedWritebackTargets,
        completedWorkWithProof: signals.completedWorkWithProof,
        economicSignals: signals.economicSignals,
        openLoops: signals.openLoops,
        outcomeLinkedItems: signals.outcomeLinkedItems,
        repeatedContextPrompts: signals.repeatedContextPrompts,
      })}`,
      recommendation: recommendationByDimension[dimension],
      severity: score < 60 ? "critical" : "warning",
      title: `${dimension.replace(/_/g, " ")} needs evidence before it can be called 95+`,
    });
  }

  return findings;
}

function buildSelfCritique(scores: AuditScores): SelfAuditPlan["self_critique"] {
  return AUDIT_DIMENSIONS.map((dimension) => {
    const score = scores[dimension];
    return {
      dimension,
      gap: score >= 95
        ? "No score gap. Preserve evidence and regression-test this dimension."
        : `Needs ${95 - score} more points of verified product evidence before claiming 95+.`,
      passed: score >= 95,
      score,
      target: 95,
    };
  });
}

function hashPlanPayload(payload: unknown): string {
  return createHash("sha256").update(JSON.stringify(payload)).digest("hex");
}

export function buildSelfAuditPlan(input: BuildSelfAuditPlanInput): SelfAuditPlan {
  if (input.imports.length === 0) {
    throw new Error("At least one AI-session import is required to run the Founder Loop audit.");
  }

  const items = extractFounderLoopItems(input.imports);
  const connectedSources = input.connectedSources ?? input.imports.map((source) => source.sourceLabel);
  const missingSources = input.missingSources ?? [];
  const signals = buildSelfAuditSignals(input.imports, items, {
    connectedSources,
    missingSources,
  });
  const scores = scoreSelfAudit(signals);
  const evidenceRefs = items.slice(0, 12).map((item) => item.evidenceRef);
  const decisions = items.filter((item) => item.type === "decision");
  const nextAction = items.find((item) => item.type === "next_action");

  const basePlan = {
    artifact_type: "ai_native_self_audit_plan" as const,
    audit_scope: {
      connected_sources: connectedSources,
      loop: "founder" as const,
      missing_sources: missingSources,
      time_window_days: input.timeWindowDays ?? 30,
    },
    extracted_items: items,
    findings: buildFindings(scores, signals),
    generated_at: input.generatedAt ?? new Date().toISOString(),
    recommended_follow_up: {
      rollback: "Delete or close the generated OrgX follow-up task if the founder rejects the audit recommendation.",
      summary: nextAction?.text ?? "Review the audit findings, approve one follow-up action, and attach proof after execution.",
      title: "Review AI-native self-audit findings and approve first follow-up",
    },
    recommended_initiative: {
      summary:
        "Generated by @useorgx/wizard audit to close the first Founder Loop from AI-session context, proof artifacts, approved writeback, and outcome review.",
      title: `Close Founder Loop: ${input.workspace.name} AI-native operating loop`,
      workstreams: [
        {
          purpose: "Capture AI-session context, decisions, commitments, and open loops.",
          tasks: [
            {
              proof_requirement: "AI-session import with cited evidence references.",
              title: "Import founder AI-session context",
            },
          ],
          title: "Founder Context Ingest",
        },
        {
          purpose: "Turn completed work into artifacts with owners, proof, and next actions.",
          tasks: [
            {
              proof_requirement: "Artifact metadata includes owner, evidence refs, and retrieval scope.",
              title: "Attach proof artifacts to completed work",
            },
          ],
          title: "Proof Chain",
        },
        {
          purpose: "Write one approved follow-up action into OrgX with rollback context.",
          tasks: [
            {
              proof_requirement: "Approved OrgX task with rollback and evidence references.",
              title: "Approve first OrgX follow-up writeback",
            },
          ],
          title: "Safe Writeback",
        },
        {
          purpose: "Record outcome and economics so the next plan learns from execution.",
          tasks: [
            {
              proof_requirement: "Founder review with time/token/value estimate and attribution confidence.",
              title: "Record outcome and economics review",
            },
          ],
          title: "Outcome Review",
        },
      ],
    },
    safe_writeback_plan: [
      {
        action: "create_follow_up_task",
        approval: "required" as const,
        rollback: "Delete or close the generated task and preserve the audit artifact as a rejected recommendation.",
        target: "orgx" as const,
      },
    ],
    schema_version: SELF_AUDIT_SCHEMA_VERSION,
    scores,
    self_critique: buildSelfCritique(scores),
    signals,
    workspace: input.workspace,
  };
  const invariant: ArtifactInvariant = {
    decision_refs: decisions.map((item) => item.evidenceRef),
    evidence_refs: evidenceRefs,
    goal_ref: `workspace:${input.workspace.id}:ai-native-self-audit`,
    next_action_ref: nextAction?.evidenceRef ?? "manual-next-action:review-audit",
    owner_ref: "founder:dri",
    proof_requirement: "verification",
    retrieval_scope:
      "Retrieve the imported AI-session context, extracted Founder Loop items, audit scores, findings, writeback plan, and outcome review.",
  };
  const artifactHash = hashPlanPayload({ ...basePlan, artifact_invariant: invariant });

  return {
    ...basePlan,
    artifact_hash: artifactHash,
    artifact_invariant: invariant,
  };
}

export function renderSelfAuditMarkdown(plan: SelfAuditPlan): string {
  const lines = [
    "# AI-Native Founder Loop Self-Audit",
    "",
    `Generated: ${plan.generated_at}`,
    `Workspace: ${plan.workspace.name} (${plan.workspace.id})`,
    `Artifact hash: ${plan.artifact_hash}`,
    "",
    "## Scores",
    "",
    "| Dimension | Score | Target | Status |",
    "| --- | ---: | ---: | --- |",
    ...AUDIT_DIMENSIONS.map((dimension) => {
      const score = plan.scores[dimension];
      return `| ${dimension.replace(/_/g, " ")} | ${score} | 95 | ${score >= 95 ? "pass" : "gap"} |`;
    }),
    "",
    "## Self-Critique",
    "",
    ...plan.self_critique.map((item) => `- ${item.dimension}: ${item.gap}`),
    "",
    "## Findings",
    "",
    ...plan.findings.map((finding) => [
      `### ${finding.title}`,
      "",
      `- Severity: ${finding.severity}`,
      `- Evidence: ${finding.evidence}`,
      `- Recommendation: ${finding.recommendation}`,
      "",
    ].join("\n")),
    "## Extracted Founder Loop Items",
    "",
    ...plan.extracted_items.map((item) => `- ${item.type}: ${item.text} (${item.evidenceRef})`),
    "",
    "## Recommended Initiative",
    "",
    `Title: ${plan.recommended_initiative.title}`,
    "",
    plan.recommended_initiative.summary,
    "",
    "## Approved Follow-Up Candidate",
    "",
    `Title: ${plan.recommended_follow_up.title}`,
    "",
    plan.recommended_follow_up.summary,
    "",
    `Rollback: ${plan.recommended_follow_up.rollback}`,
    "",
    "## Artifact Invariant",
    "",
    "```json",
    JSON.stringify(plan.artifact_invariant, null, 2),
    "```",
    "",
  ];

  return lines.join("\n");
}

function parseEntityRef(payload: unknown): OrgxEntityRef {
  const entity = isRecord(payload) && isRecord(payload.data) ? payload.data : payload;
  if (!isRecord(entity)) {
    throw new Error("OrgX returned an unexpected entity payload.");
  }

  const id = typeof entity.id === "string" ? entity.id : "";
  const title = typeof entity.title === "string"
    ? entity.title
    : typeof entity.name === "string"
      ? entity.name
      : "";
  if (!id || !title) {
    throw new Error("OrgX returned an incomplete entity payload.");
  }

  return { id, title };
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function formatHttpError(status: number, body: unknown): string {
  if (typeof body === "string" && body.trim().length > 0) {
    return `HTTP ${status}: ${body}`;
  }
  if (isRecord(body) && typeof body.error === "string" && body.error.trim().length > 0) {
    return `HTTP ${status}: ${body.error}`;
  }
  if (isRecord(body) && isRecord(body.error) && typeof body.error.message === "string") {
    return `HTTP ${status}: ${body.error.message}`;
  }
  return `HTTP ${status}`;
}

async function createOrgxEntity(
  body: Record<string, unknown>,
  options: OrgxWriteOptions,
): Promise<OrgxEntityRef> {
  if (options.dryRun) {
    return {
      id: `dry-run-${String(body.type ?? "entity")}`,
      title: String(body.title ?? body.name ?? "Dry-run entity"),
    };
  }

  const auth = await resolveOrgxAuth(options);
  if (!auth) {
    throw new Error("No OrgX API key configured. Run `wizard auth login` before using writeback flags.");
  }

  const response = await fetch(buildOrgxApiUrl("/entities", auth.baseUrl), {
    body: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    method: "POST",
    signal: AbortSignal.timeout(15_000),
  });
  const responseBody = await parseResponseBody(response);
  if (!response.ok) {
    throw new Error(`Failed to create ${String(body.type ?? "entity")}. ${formatHttpError(response.status, responseBody)}`);
  }

  return parseEntityRef(responseBody);
}

export async function createAuditArtifact(
  options: CreateAuditArtifactOptions,
): Promise<OrgxEntityRef> {
  const { initiativeId, markdown, plan } = options;
  return createOrgxEntity(
    {
      artifact_type: "shared.project_handbook",
      description: "AI-native Founder Loop self-audit generated by @useorgx/wizard audit.",
      entity_id: initiativeId,
      entity_type: "initiative",
      external_url: `orgx-wizard://audit/${plan.artifact_hash}`,
      initiative_id: initiativeId,
      metadata: {
        ...plan.artifact_invariant,
        artifact_hash: plan.artifact_hash,
        atomic_unit_type: "ai_native_self_audit",
        completion_state: "generated",
        schema_validated: true,
        scores: plan.scores,
      },
      name: `AI-Native Self-Audit: ${plan.workspace.name}`,
      preview_markdown: markdown.slice(0, 8000),
      status: "in_review",
      type: "artifact",
      workspace_id: plan.workspace.id,
    },
    options,
  );
}

export async function createInitiativeFromAuditPlan(
  options: CreateAuditInitiativeOptions,
): Promise<OrgxEntityRef> {
  const { plan } = options;
  return createOrgxEntity(
    {
      metadata: {
        artifact_hash: plan.artifact_hash,
        audit_score_snapshot: plan.scores,
        source: "ai_native_self_audit",
        source_artifact_type: "ai_native_self_audit_plan",
      },
      status: "active",
      summary: plan.recommended_initiative.summary,
      title: plan.recommended_initiative.title,
      type: "initiative",
      workspace_id: plan.workspace.id,
    },
    options,
  );
}

export async function createAuditFollowUpTask(
  options: CreateAuditFollowUpOptions,
): Promise<OrgxEntityRef> {
  const { initiativeId, milestoneId, plan, workstreamId } = options;
  const resolvedWorkstreamId = workstreamId?.trim() || (
    await createOrgxEntity(
      {
        initiative_id: initiativeId,
        metadata: {
          artifact_hash: plan.artifact_hash,
          source: "ai_native_self_audit",
        },
        status: "active",
        summary: "Follow-up workstream created by @useorgx/wizard audit so the approved action has execution context.",
        title: "AI-native self-audit follow-up",
        type: "workstream",
        workspace_id: plan.workspace.id,
      },
      options,
    )
  ).id;
  const resolvedMilestoneId = milestoneId?.trim() || (
    await createOrgxEntity(
      {
        initiative_id: initiativeId,
        metadata: {
          artifact_hash: plan.artifact_hash,
          source: "ai_native_self_audit",
        },
        status: "planned",
        summary: "Milestone created by @useorgx/wizard audit so the approved follow-up task has proof-chain hierarchy.",
        title: "AI-native self-audit follow-up",
        type: "milestone",
        workspace_id: plan.workspace.id,
        workstream_id: resolvedWorkstreamId,
      },
      options,
    )
  ).id;

  return createOrgxEntity(
    {
      description: `${plan.recommended_follow_up.summary}\n\nRollback: ${plan.recommended_follow_up.rollback}`,
      initiative_id: initiativeId,
      milestone_id: resolvedMilestoneId,
      metadata: {
        ...plan.artifact_invariant,
        artifact_hash: plan.artifact_hash,
        source: "ai_native_self_audit",
        source_action: "approved_follow_up_writeback",
      },
      priority: "high",
      status: "todo",
      title: plan.recommended_follow_up.title,
      type: "task",
      workstream_id: resolvedWorkstreamId,
      workspace_id: plan.workspace.id,
    },
    options,
  );
}

export function normalizeAuditBaseUrl(value?: string): string {
  return normalizeOrgxBaseUrl(value);
}
